import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";


import { formatDate, formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { EnumMap } from "../../../../../../common/enum-map";
import { IEnumResponse } from "../../../../../../resources-vue/vue-interfaces/i-enum";
import { IGetImplantableDeviceResponse, PatientService } from "../../../../../../services/patient.service";
import { ImplantableDevice } from "../../../../../../models/pal-health-history/implantable-device";
import { ToastrService } from "../../../../../../services/toastr.service";

import TablePrompt from "../../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import ImplantableValidationSection from "../ImplantableValidationSection/ImplantableValidationSection.vue";
import EmptyTextHandler from "../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        TablePrompt,
        DatePicker,
        ImplantableValidationSection,
        EmptyTextHandler
    },

    props: {
        implantableDevice: {
            type:Object as PropType<IGetImplantableDeviceResponse>
        },
        deviceStatusEnum:{
            type:Array<IEnumResponse>
        },
        patientId:{
           type: String
        },
        resetList:{
            type:Function
        },
        accountId:{
            type:String
        },
        application:{
            type:Number
        },
        columns:{
            type:Number
        }
    },

    data(vm) {
        return {
            _toastrService:null,
            _patientService:null,
            isEditRequested: false as boolean,
            isViewDetailsRequested: false as boolean,
            isDeleteRequested: false as boolean,
            isActivateRequested: false as boolean,
            isInactivateRequested: false as boolean,
            isEditInProgress: false as boolean,
            isActivateInProgress: false as boolean,
            isDeleteInProgress: false as boolean,
            isInactivateInProgress: false as boolean,
            deviceStatusEnumMap: null,
            cacheImplantableDevice:null,
            formatDate,
            formatDateInput
        };
    },

    created() {
        this._patientService=Container.instance.get(PatientService);
        this._toastrService=Container.instance.get(ToastrService)
        this.deviceStatusEnumMap = new EnumMap(this.deviceStatusEnum);
    },

    methods: {
        openEdit() {
            this.isEditRequested = true;
            this.cacheImplantableDevice = new ImplantableDevice(this.implantableDevice);
        },
        requestDelete() {
            this.isDeleteRequested = true;
        },

        openViewDetails() {
            this.isViewDetailsRequested = true;
        },

        openConfirmActivate() {
            this.isActivateRequested = true;
        },

       openConfirmInactivate() {
            this.isInactivateRequested = true;
        },

        closeEdit() {
            this.cacheImplantableDevice =null;
            this.isEditRequested = false;
        },

         async updateImplantableDevice() {
            try {
                this.isEditInProgress = true;
                const payload={
                    Application:this.application,
                    AccountId:this.accountId,
                    DateOfImplant:this.implantableDevice.DateOfImplant
                }
                await this._patientService.updateImplantableDevice(this.patientId, this.implantableDevice.Id,payload);
                this.closeEdit();
                this.resetList();
                this._toastrService.success({
                    title: `Implantable Device Updated`,
                    message: `The implantable device has been successfully updated.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Error - Update Implantable Device`,
                    message: `There was a problem while updating the implantable device. Please try again.`
                });
            } finally {
                this.isEditInProgress = false;
            }
        },
         async activateDevice() {
            try {
                this.isActivateInProgress = true;
                const payload={
                    Application:this.application,
                    AccountId:this.accountId,
                    Status:1
                }
                await this._patientService.updateImplatableDeviceStatus(this.implantableDevice.Id,payload);
                this.resetList();
                this._toastrService.success({
                    title: `Implantable Device Activated`,
                    message: `The implantable device has been successfully activated.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Error - Activate Implantable Device`,
                    message: `There was a problem while activating the implantable device. Please try again.`
                });
            } finally {
                this.isActivateInProgress = false;
            }
        },
        closeConfirmActivate() {
            this.isActivateRequested = false;
        },

      async deleteDevice() {
            try {
                this.isDeleteInProgress = true;
                const payload={
                    Application:this.application,
                    AccountId:this.accountId
                }
                await this._patientService.deleteImplantableDevice(this.implantableDevice.Id,payload);
                this.closeRequestDelete();
                this.resetList();
                this._toastrService.success({
                    title: `Implantable Device Deleted`,
                    message: `The implantable device has been successfully deleted.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Error - Delete Implantable Device`,
                    message: `There was a problem while deleting the implantable device. Please try again.`
                });
            } finally {
                this.isDeleteInProgress = false;
            }
        },

        closeRequestDelete() {
            this.isDeleteRequested = false;
        },

        async inactivateDevice() {
            try {
                this.isInactivateInProgress = true;
                const payload={
                    Application:this.application,
                    AccountId:this.accountId,
                    Status:2
                }
                await this._patientService.updateImplatableDeviceStatus(this.implantableDevice.Id,payload);
                this.closeConfirmInactivate()
                this.resetList();
                this._toastrService.success({
                    title: `Implantable Device Inactivated`,
                    message: `The implantable device has been successfully inactivated.`
                });
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Error - Inactivate Implantable Device`,
                    message: `There was a problem while inactivating the implantable device. Please try again.`
                });
            } finally {
                this.isInactivateInProgress = false;
            }
        },
        closeConfirmInactivate() {
            this.isInactivateRequested = false;
        },
        closeViewDetails() {
            this.isViewDetailsRequested = false;
        },
        handleDateChange(date: string) {
            this.implantableDevice.DateOfImplant = date;
        },
        handleDateInputChange(e:any){
            this.implantableDevice.DateOfImplant=e.target.value
        }
    }
});
